import React, {
    useState, useContext, useEffect, useCallback } from 'react';
import moment from 'moment';
import { Link, withRouter } from 'react-router-dom';
import _ from 'lodash';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { withViewModelService, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { SearchService } from 'gw-capability-gateway';
import { MLPolicyTypeUtil } from 'ml-portals-util-react';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { ServiceManager } from '@jutro/legacy/services';

import MLViewAllPoliciesAdditionalFilter from './MLViewAllPoliciesAdditionalFilter';

import styles from './MLViewAllPoliciesPage.module.scss';
import messages from './MLViewAllPoliciesPage.messages';
import metadata from './MLViewAllPoliciesPage.metadata.json5';

import { IntlContext } from '@jutro/legacy/locale';

const MAX_SEARCH_RESULT_DISPLAY = 20;

const MLViewAllPoliciesPage = () => {

    const translator = useTranslator();
    const intl = useContext(IntlContext);
    const { authHeader } = useAuthentication();
    const localeService = ServiceManager.getService('locale-service');
    const viewModelService = useContext(ViewModelServiceContext);

    const productCodes = [
        MLProductCodeUtil.getPersonalAutoCode(),
        MLProductCodeUtil.getPersonalUmbrellaCode(),
        MLProductCodeUtil.getDwellingCode(),
        MLProductCodeUtil.getRentalCode(),
        MLProductCodeUtil.getCondoCode(),
        MLProductCodeUtil.getLandlordHome(),
        MLProductCodeUtil.getLandlordCondo()
    ];
    const country = localeService.getDefaultCountryCode();
    const countryList = viewModelService.productMetadata.get('pc').types.getTypelist('Country');
    const initStateList = viewModelService.productMetadata.get('pc').types.getTypelist('State');
    const stateList = _.filter(initStateList.codes, (state) => state.belongsToCategory(countryList.getCode(country)));
    const { getPolicyTypeName } = MLPolicyTypeUtil();

    const [isLoading, updateIsLoading] = useState(false);
    const [agentHasGroupPermission, updateAgentHasGroupPermission] = useState(false);
    const [policies, updatePolicies] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({country_ML: country});

    const loadPoliciesData = useCallback(async (request) => {
        updateIsLoading(true);
        if (request.groupName_ML || (!_.isUndefined(request.groupName_ML) && request.groupName_ML.length > 0)) {
            updateAgentHasGroupPermission(true);
        }
        const response = await SearchService.searchAvailablePolicyFromCurrentUser(request, authHeader);
        updatePolicies(response.searchPolicyDetails);
        updateResultsRowCount(response.searchPolicyDetails.length);
        updateIsLoading(false);
    }, [authHeader]);

    useEffect(() => {
        loadPoliciesData({country_ML: country});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const onFilterClick = () => {
        loadPoliciesData(filterData);
    };

    const onClearFilterClick = () => {
        updateFilterData({country_ML: country});
        updatePolicies([]);
        updateResultsRowCount(0);
    };

    const convertLocalDateToDate = (localDate) => {
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format('MM/DD/YYYY');
    };

    const formatAmountString = (amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    };

    const renderEffectiveDateCell = (item) => {
        return convertLocalDateToDate(item.effectiveDate);
    };

    const renderExpirationlDateCell = (item) => {
        return convertLocalDateToDate(item.expirationDate);
    };

    const renderCustomerNameCell = (item) => {
        return (
            <Link to={`/accounts/${item.accountDetails.accountNumber}/summary`} className={styles.linkCellValue}>
                {_.capitalize(item.accountName)}
            </Link>
        )
    };

    const renderPolicyTypeCell = (item) => {
        return getPolicyTypeName(item.policyType);
    };

    const renderPolicyNumberCell = (item) => {
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {item.policyNumber}
            </Link>
        )
    };

    const renderPremiumCell = (item) => {
        return formatAmountString(item.premium);
    };

    const overrideProps = {
        backToDashboard: {
            content: (
                <Link to='/' className={styles.backToDashboardLink}>
                    <i className="icon-ml-right-carrot-icon icon-rotate-180" />&nbsp;
                    {translator(messages.backToDashboard)}
                </Link>
            )
        },
        viewAllPoliciesResultHeader: {
            content: (
                <FormattedMessage
                    {...messages.weFoundxCountResults}
                    values={{
                        spanResult: <span className="ml-pill ml-pill-default-inverse large margin-right-0">{policies.length}</span>
                    }}
                />
            )
        },
        policiesFilterComponent: {
            filterData,
            productCodes, 
            onFilter: onFilterClick,
            onClearFilters: onClearFilterClick,
            resultsRowCount,
            updateFilterData,
            additionalFiltersComponent: MLViewAllPoliciesAdditionalFilter,
            additionalFilterProps: {
                stateList
            },
            dateLabel: translator(messages.effectiveDateRange),
            showSeparator: true
        },
        statusDesc: {
            visible: isLoading,
            content: translator(messages.loading)
        },
        policiesDataTable: {
            data: _.orderBy(policies, ['effectiveDate.year', 'effectiveDate.month', 'effectiveDate.day'], ['desc', 'desc', 'desc']),
            visible: !isLoading,
            pageSizeOptions: [MAX_SEARCH_RESULT_DISPLAY],
            defaultConfig: {"pageSize": MAX_SEARCH_RESULT_DISPLAY, "page": 0},
            noDataText: agentHasGroupPermission ? translator(messages.errorPermissionGroup) : translator(messages.noPoliciesToShow),
            tableLabel: translator(messages.allPolicies)
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderEffectiveDateCell,
            renderExpirationlDateCell,
            renderCustomerNameCell,
            renderPolicyTypeCell,
            renderPolicyNumberCell,
            renderPremiumCell
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

export default withRouter(withViewModelService(MLViewAllPoliciesPage));
